import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { MainActions } from 'screens/main';
import {
  usePushLog,
  useRealtimeJobLimits,
  useReduxSelector,
  useTotalSalary,
} from 'services/hooks';
import { callFunctionAction } from 'services/api';
import { PlayerActions } from 'services/redux';
import { arrayEqual, objectEqual } from 'services/utils';

import { CompanyModal } from '../../company.typing';

import { toDisplayCompany } from './company-card.utils';
import { IUseCompanyCardProps } from './company-card.typing';

export const useCompanyCard = (props: IUseCompanyCardProps) => {
  const {
    valuesInfo,
    sector,
    job,
    companyName,
    companyBonus,
    onSetModal,
    onReject,
  } = props;

  const gameCode = useReduxSelector((redux) => redux.player.authInfo.code);
  const isSelfPaced = useReduxSelector(
    (redux) => redux.game.config?.isSelfPaced
  );
  const email = useReduxSelector((redux) => redux.player.authInfo.email);
  const constValues = useReduxSelector(
    (redux) => redux.app.values,
    objectEqual
  );
  const chValues = useReduxSelector(
    (redux) => redux.player.createCharacter.values,
    arrayEqual
  );
  const constAnchors = useReduxSelector(
    (redux) => redux.app.anchors,
    arrayEqual
  );
  const chAnchors = useReduxSelector(
    (redux) => redux.player.createCharacter.careerAnchors,
    arrayEqual
  );

  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const pushLog = usePushLog();
  const isLimited = useRealtimeJobLimits(sector);

  const { totalSalary } = useTotalSalary({ job, sector, companyBonus });

  const displayCompany = useMemo(
    () =>
      toDisplayCompany(
        {
          negative: valuesInfo.negative,
          positive: valuesInfo.positive,
        },
        { constAnchors, chAnchors, constValues, chValues }
      ),
    [
      chAnchors,
      chValues,
      constAnchors,
      constValues,
      valuesInfo.negative,
      valuesInfo.positive,
    ]
  );

  const onAccept = async () => {
    if (!gameCode || !email || isLoading) {
      throw new Error('Unauthorized');
    }

    setIsLoading(true);

    try {
      const result = await callFunctionAction('@WORK/GET_JOB', {
        sector,
        job,
        companyName,
      });

      dispatch(PlayerActions.merge(result.mergePlayer));

      pushLog({
        type: 'work',
        action: 'newjob',
        params: {
          position: job,
        },
      });

      onSetModal(CompanyModal.Congrats);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = useCallback(() => {
    onReject();

    if (isSelfPaced) {
      dispatch(MainActions.update({ isActing: false }));
    }
  }, [dispatch, isSelfPaced, onReject]);

  return { displayCompany, isLimited, totalSalary, onAccept, handleReject };
};
